import { useCallback, useEffect, useRef } from "react";

/** usePrivacyMode — T5 · PHI masking across every ambient surface.
 *  Persisted per-user in localStorage. `mask(text)` collapses name→initials,
 *  address→city, phone→last-4. Every surface that renders PHI calls mask().
 */
const KEY = "astat.floor.privacy";
export function usePrivacyMode() {
  const listeners = useRef(new Set());
  const on = () => { try { return localStorage.getItem(KEY) === "1"; } catch { return false; } };
  const set = useCallback((v) => {
    try { localStorage.setItem(KEY, v ? "1" : "0"); } catch { /* noop */ }
    listeners.current.forEach(fn => fn(v));
    window.dispatchEvent(new CustomEvent("astat-privacy", { detail: { on: v } }));
  }, []);
  useEffect(() => {
    const fn = () => { /* trigger consumers to re-read */ };
    window.addEventListener("astat-privacy", fn);
    return () => window.removeEventListener("astat-privacy", fn);
  }, []);
  return { on: on(), toggle: () => set(!on()), set };
}

export function maskName(name) {
  if (!name) return "";
  if (!isOn()) return name;
  return String(name).split(/\s+/).map(w => (w[0] || "") + ".").join(" ");
}
export function maskPhone(phone) {
  if (!phone) return "";
  if (!isOn()) return phone;
  const d = String(phone).replace(/\D/g, "");
  return d.length >= 4 ? `••• ${d.slice(-4)}` : "•••";
}
export function maskAddress(addr) {
  if (!addr) return "";
  if (!isOn()) return addr;
  const parts = String(addr).split(",");
  return parts.length >= 2 ? `••• ${parts[parts.length - 2].trim()}` : "•••";
}
function isOn() { try { return localStorage.getItem(KEY) === "1"; } catch { return false; } }
